import { createFileRoute } from '@tanstack/react-router'
import { Settings, Users, Shield, Bell, Database, FileText, User, Zap } from 'lucide-react'

import { useCurrentUser } from '@/hooks'

export const Route = createFileRoute('/_authenticated/settings/' as never)({
  component: SettingsIndexPage,
})

function SettingsIndexPage() {
  const { data: user } = useCurrentUser()
  const isDirector = user?.role === 'director'

  const settingsModules = [
    {
      title: '系统配置',
      description: '系统参数和基础配置',
      icon: Database,
      href: '/settings/config',
      color: 'bg-green-500',
      disabled: !isDirector,
      directorOnly: true,
    },
    {
      title: '系统通知设置',
      description: '配置系统通知渠道和触发器',
      icon: Bell,
      href: '/settings/notifications',
      color: 'bg-orange-500',
      disabled: !isDirector,
      directorOnly: true,
    },
    {
      title: '我的通知偏好',
      description: '配置您的通知接收方式',
      icon: User,
      href: '/settings/my-notifications',
      color: 'bg-blue-500',
      disabled: false,
      directorOnly: false,
    },
    {
      title: '效率设置',
      description: '配置效率提升功能和快捷键',
      icon: Zap,
      href: '/settings/efficiency',
      color: 'bg-gradient-to-br from-yellow-400 to-orange-500',
      disabled: false,
      directorOnly: false,
    },
    {
      title: '审计日志',
      description: '查看系统操作记录',
      icon: FileText,
      href: '/settings/audit-logs',
      color: 'bg-purple-500',
      disabled: !isDirector,
      directorOnly: true,
    },
    {
      title: '用户管理',
      description: '管理系统用户和权限',
      icon: Users,
      href: '/settings/users',
      color: 'bg-cyan-500',
      disabled: true,
      directorOnly: true,
    },
    {
      title: '角色权限',
      description: '配置角色和权限设置',
      icon: Shield,
      href: '/settings/roles',
      color: 'bg-pink-500',
      disabled: true,
      directorOnly: true,
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">系统设置</h1>
        <p className="text-muted-foreground">管理系统配置和用户权限</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {settingsModules.map((module) => {
          const content = (
            <>
              <div
                className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg ${module.color}`}
              >
                <module.icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="mb-1 font-semibold">{module.title}</h3>
              <p className="text-sm text-muted-foreground">{module.description}</p>
              {module.disabled && module.directorOnly && !isDirector && (
                <span className="mt-2 inline-block rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  仅主管可访问
                </span>
              )}
              {module.disabled && !module.directorOnly && (
                <span className="mt-2 inline-block rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                  即将推出
                </span>
              )}
            </>
          )

          if (module.disabled) {
            return (
              <div
                key={module.href}
                className="cursor-not-allowed rounded-lg border bg-card p-6 opacity-50"
              >
                {content}
              </div>
            )
          }

          return (
            <a
              key={module.href}
              href={module.href}
              className="group rounded-lg border bg-card p-6 transition-colors hover:bg-muted/50"
            >
              {content}
            </a>
          )
        })}
      </div>

      <div className="rounded-lg border bg-muted/30 p-6">
        <div className="flex items-start gap-4">
          <Settings className="h-8 w-8 text-muted-foreground" />
          <div>
            <h3 className="font-semibold">系统信息</h3>
            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
              <p>版本: 1.0.0</p>
              <p>环境: 开发环境</p>
              <p>时区: UTC+4 (迪拜)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
